/**
 * PasswordField Component
 * Enhanced password input with show/hide toggle functionality
 * Built on top of FormField for consistency
 */

import { forwardRef, useState } from 'react';
import { FormField, type FormFieldProps } from './FormField';
import { cn } from '../../lib/utils';

export interface PasswordFieldProps extends Omit<FormFieldProps, 'type'> {
  showToggle?: boolean;
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  ({ showToggle = true, className, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);

    const togglePassword = () => {
      setShowPassword(!showPassword);
    };

    if (!showToggle) {
      return (
        <FormField ref={ref} type="password" className={className} {...props} />
      );
    }

    return (
      <div>
        <label
          htmlFor={props.id || 'password'}
          className="block text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          {props.label}
        </label>
        <div className="mt-1 relative">
          <input
            ref={ref}
            type={showPassword ? 'text' : 'password'}
            id={props.id || 'password'}
            disabled={props.isLoading || props.disabled}
            className={cn(
              'appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900 focus:ring-sky-500 focus:border-sky-500 dark:focus:border-sky-400 disabled:bg-gray-50 dark:disabled:bg-gray-800 disabled:text-gray-500 dark:disabled:text-gray-400 text-base sm:text-sm touch-manipulation transition-colors duration-200',
              // Text and background colors
              'text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800',
              // High contrast support
              'high-contrast:border-black high-contrast:focus:border-black high-contrast:focus:ring-black',
              props.error &&
                'border-red-300 dark:border-red-700 focus:border-red-500 dark:focus:border-red-400 focus:ring-red-500 dark:focus:ring-red-400 high-contrast:border-red-700 high-contrast:focus:border-red-700 high-contrast:focus:ring-red-700',
              className
            )}
            aria-invalid={props.error ? 'true' : 'false'}
            aria-describedby={
              props.error
                ? `${props.id || 'password'}-error`
                : props.helpText
                  ? `${props.id || 'password'}-help`
                  : undefined
            }
            placeholder={props.placeholder}
            value={props.value}
            onChange={props.onChange}
            onBlur={props.onBlur}
            name={props.name}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center justify-center w-10 h-full min-h-[44px] touch-manipulation rounded-r-md focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 high-contrast:focus:ring-4 high-contrast:focus:ring-offset-4 transition-colors duration-200"
            onClick={togglePassword}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                togglePassword();
              }
            }}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            aria-pressed={showPassword}
            tabIndex={0}
          >
            {showPassword ? (
              <svg
                className="h-5 w-5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                />
              </svg>
            ) : (
              <svg
                className="h-5 w-5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.543 7-1.275 4.057-5.065 7-9.543 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            )}
          </button>
        </div>
        {props.error && (
          <p
            id={`${props.id || 'password'}-error`}
            className="mt-1 text-sm text-red-600 dark:text-red-400"
            role="alert"
          >
            {props.error}
          </p>
        )}
        {props.helpText && !props.error && (
          <p
            id={`${props.id || 'password'}-help`}
            className="mt-1 text-xs text-gray-500 dark:text-gray-400"
          >
            {props.helpText}
          </p>
        )}
      </div>
    );
  }
);

PasswordField.displayName = 'PasswordField';
